import { motion } from "framer-motion";
import { User, Users, Presentation, ArrowRight, Lock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

const services = [
  {
    icon: User,
    name: "1:1 Personal Session",
    tag: "Private. Focused. Just about your money.",
    desc: "A 30-minute one-on-one video or call session — best for individuals who want personalized guidance on budgeting, savings, debt, or getting started with investing.",
    price: "₹999",
    priceNote: "per 30-min session",
    cta: "Pay ₹999 & Book",
    action: "payment",
  },
  {
    icon: Users,
    name: "Group Sessions",
    tag: "Learn together. Save together.",
    desc: "An interactive session for small groups (4–10 people) — friends, colleagues, or families — covering budgeting, saving habits, and investment fundamentals.",
    price: "Custom",
    priceNote: "based on group size",
    cta: "Enquire for a Group",
    action: "book",
  },
  {
    icon: Presentation,
    name: "Awareness Talks",
    tag: "For classrooms, communities & organizations.",
    desc: "A structured talk for schools, colleges, RWAs, corporate teams, or community events — delivering foundational financial literacy with real, relatable examples.",
    price: "Quote",
    priceNote: "by audience & format",
    cta: "Enquire for a Talk",
    action: "book",
  },
];

const ServicesGrid = () => {
  const scrollToBook = () => scrollToSection("#book");

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-background px-6 py-24 md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent md:text-sm">
            Three Ways to Work Together
          </p>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-tight tracking-tight text-primary md:text-6xl">
            Pick what fits you — book separately below.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                data-testid={`service-card-${i}`}
                className="flex min-h-[440px] flex-col justify-between rounded-sm border border-border bg-card p-9 transition-colors duration-300 hover:border-primary"
              >
                <div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <s.icon size={22} />
                  </div>
                  <h3 className="mt-8 font-display text-3xl font-medium text-primary">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-accent">
                    {s.tag}
                  </p>
                  <p className="mt-5 text-base font-light leading-relaxed text-foreground/75">
                    {s.desc}
                  </p>
                </div>

                <div className="mt-8">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-4xl font-semibold text-primary">
                      {s.price}
                    </span>
                    <span className="text-sm text-muted-foreground">{s.priceNote}</span>
                  </div>
                  <button
                    data-testid={`service-cta-${i}`}
                    onClick={() =>
                      s.action === "payment"
                        ? window.open(SITE.payment1on1, "_blank", "noopener")
                        : scrollToBook()
                    }
                    className="group mt-6 flex w-full items-center justify-between rounded-full border border-primary px-6 py-3.5 text-sm font-semibold text-primary transition-colors duration-300 hover:bg-primary hover:text-primary-foreground"
                  >
                    {s.cta}
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </button>
                  {s.action === "payment" && (
                    <p
                      data-testid={`service-secure-${i}`}
                      className="mt-3 flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground"
                    >
                      <Lock size={13} className="text-primary" />
                      Secure payment via Easebuzz
                    </p>
                  )}
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
